import { lazy, Suspense, useEffect, ReactNode } from "react";
import BrandedSpinner from "@/components/BrandedSpinner";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { AppUpdateGate } from "@/components/AppUpdateGate";
import { initPushNotifications } from "@/lib/push-notifications";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { logFrontendError, tryGetErrorLogContext } from "@/hooks/use-error-logger";
import { initGlobalErrorHandlers } from "@/lib/global-error-handler";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { AuthProvider, RequireAuth, RequireSubscription } from "@/lib/auth";
import { RequireFranchiseUser, RequireFranchiseSubscription, FranchiseBasePathProvider } from "@/lib/franchise-auth";
import { StoreContextProvider, useStoreContext } from "@/hooks/use-store-context";
import { getPortal } from "@/lib/subdomain";
const HomePage = lazy(() => import("./pages/HomePage"));
const AdminApp = lazy(() => import("./admin/AdminApp"));
const FranchiseApp = lazy(() => import("./franchise/FranchiseApp"));
const FranchiseDashboard = lazy(() => import("./franchise/pages/FranchiseDashboard"));
const FranchiseOrgs = lazy(() => import("./franchise/pages/FranchiseOrgs"));
const FranchiseSpend = lazy(() => import("./franchise/pages/FranchiseSpend"));
const FranchiseWaste = lazy(() => import("./franchise/pages/FranchiseWaste"));
const FranchiseVendors = lazy(() => import("./franchise/pages/FranchiseVendors"));
const FranchiseProducts = lazy(() => import("./franchise/pages/FranchiseProducts"));
const FranchiseSubscriptionPage = lazy(() => import("./franchise/pages/FranchiseSubscription"));
const FranchiseCatalog = lazy(() => import("./franchise/pages/FranchiseCatalog"));
import FranchiseLayout from "./franchise/FranchiseLayout";
import DashboardLayout from "./components/DashboardLayout";

const Auth = lazy(() => import("./pages/Auth"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const FranchiseSignup = lazy(() => import("./pages/FranchiseSignup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Stores = lazy(() => import("./pages/dashboard/Stores"));
const StoreDetail = lazy(() => import("./pages/dashboard/StoreDetail"));
const Products = lazy(() => import("./pages/dashboard/Products"));
const ProductHub = lazy(() => import("./pages/dashboard/ProductHub"));
const Inventory = lazy(() => import("./pages/dashboard/Inventory"));
const Reorder = lazy(() => import("./pages/dashboard/Reorder"));
const Orders = lazy(() => import("./pages/dashboard/Orders"));
const WasteLog = lazy(() => import("./pages/dashboard/WasteLog"));
const CountHistory = lazy(() => import("./pages/dashboard/CountHistory"));
const Deliveries = lazy(() => import("./pages/dashboard/Deliveries"));
const Transfers = lazy(() => import("./pages/dashboard/Transfers"));
const Team = lazy(() => import("./pages/dashboard/Team"));
const Vendors = lazy(() => import("./pages/dashboard/Vendors"));
const Analytics = lazy(() => import("./pages/dashboard/Analytics"));
const StoreComparison = lazy(() => import("./pages/dashboard/StoreComparison"));
const Tasks = lazy(() => import("./pages/dashboard/Tasks"));
const Recipes = lazy(() => import("./pages/dashboard/Recipes"));
const Subscription = lazy(() => import("./pages/dashboard/Subscription"));
const OrgSettings = lazy(() => import("./pages/dashboard/OrgSettings"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const ChoosePortal = lazy(() => import("./pages/ChoosePortal"));
const VendorPortal = lazy(() => import("./pages/VendorPortal"));
const VendorConfirm = lazy(() => import("./pages/VendorConfirm"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

function logQueryError(error: Error, source: "api") {
  tryGetErrorLogContext().then((ctx) => {
    if (!ctx) return;
    logFrontendError({
      organizationId: ctx.orgId,
      userId: ctx.userId,
      error: `${error.name}: ${error.message}`,
      errorType: error.name,
      errorSource: source,
      stackTrace: error.stack,
    });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,        // 1 min — inventory data changes slowly
      gcTime: 10 * 60_000,      // keep unused queries in memory for 10 min
      retry: 1,
      refetchOnWindowFocus: false, // tab switches no longer blast all queries
    },
  },
  queryCache: new QueryCache({
    onError: (error) => logQueryError(error as Error, "api"),
  }),
  mutationCache: new MutationCache({
    onError: (error) => logQueryError(error as Error, "api"),
  }),
});

const ContentSpinner = () => <BrandedSpinner size="section" />;

// Role hierarchy: owner > manager > staff > viewer
const ROLE_RANK: Record<string, number> = { viewer: 0, staff: 1, manager: 2, owner: 3 };

/**
 * Redirects to /dashboard if the user's effectiveRole is below the required minimum.
 * Must be rendered inside StoreContextProvider (i.e. inside DashboardShell).
 */
function RequireMinRole({ children, minRole }: { children: ReactNode; minRole: string }) {
  const { effectiveRole } = useStoreContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (effectiveRole !== null && (ROLE_RANK[effectiveRole] ?? -1) < (ROLE_RANK[minRole] ?? 0)) {
      navigate("/dashboard", { replace: true });
    }
  }, [effectiveRole, minRole, navigate]);

  if (effectiveRole === null) return null;
  if ((ROLE_RANK[effectiveRole] ?? -1) < (ROLE_RANK[minRole] ?? 0)) return null;
  return <>{children}</>;
}

function DashboardShell({ children, requireSub = true }: { children: React.ReactNode; requireSub?: boolean }) {
  return (
    <RequireAuth>
      <StoreContextProvider>
        <DashboardLayout>
          <Suspense fallback={<ContentSpinner />}>
            {requireSub ? <RequireSubscription>{children}</RequireSubscription> : children}
          </Suspense>
        </DashboardLayout>
      </StoreContextProvider>
    </RequireAuth>
  );
}

// Franchise portal embedded in main app — used when running as native app or
// when a franchise user logs into the regular app URL (Option B role detection).
function FranchisePortalShell() {
  return (
    <RequireAuth>
      <FranchiseBasePathProvider basePath="/franchise-portal">
        <RequireFranchiseUser>
          <RequireFranchiseSubscription>
          <FranchiseLayout>
            <Suspense fallback={<ContentSpinner />}>
              <Routes>
                <Route path="/" element={<FranchiseDashboard />} />
                <Route path="/orgs" element={<FranchiseOrgs />} />
                <Route path="/spend" element={<FranchiseSpend />} />
                <Route path="/waste" element={<FranchiseWaste />} />
                <Route path="/vendors" element={<FranchiseVendors />} />
                <Route path="/products" element={<FranchiseProducts />} />
                <Route path="/catalog" element={<FranchiseCatalog />} />
                <Route path="/billing" element={<FranchiseSubscriptionPage />} />
              </Routes>
            </Suspense>
          </FranchiseLayout>
          </RequireFranchiseSubscription>
        </RequireFranchiseUser>
      </FranchiseBasePathProvider>
    </RequireAuth>
  );
}

function PushNotificationInit() {
  const navigate = useNavigate();
  useEffect(() => {
    initPushNotifications(navigate).catch(() => {});
  }, [navigate]);
  return null;
}

function GlobalErrorHandlerInit() {
  useEffect(() => {
    initGlobalErrorHandlers();
  }, []);
  return null;
}

const App = () => {
  const portal = getPortal();

  // Franchise subdomain — hand off entirely to FranchiseApp
  if (portal === "franchise") {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <ChunkErrorBoundary>
                <Suspense fallback={<BrandedSpinner size="full" />}>
                  <FranchiseApp />
                </Suspense>
              </ChunkErrorBoundary>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <AppUpdateGate />
        <AuthProvider>
          <GlobalErrorHandlerInit />
          <PushNotificationInit />
          <AppErrorBoundary>
          <ChunkErrorBoundary>
          <Suspense fallback={<BrandedSpinner size="full" />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/confirm" element={<AuthConfirm />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/franchise/signup" element={<FranchiseSignup />} />
              <Route path="/signup" element={<FranchiseSignup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/choose-portal" element={<RequireAuth><ChoosePortal /></RequireAuth>} />
              {/* Admin portal — path-based routing */}
              <Route path="/admin/*" element={<AdminApp />} />
              {/* Franchise portal — role-based, for main app / mobile (Option B) */}
              <Route path="/franchise-portal/*" element={<FranchisePortalShell />} />
              <Route path="/dashboard" element={<DashboardShell><Dashboard /></DashboardShell>} />
              {/* manager+ only */}
              <Route path="/dashboard/stores" element={<DashboardShell><RequireMinRole minRole="manager"><Stores /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/stores/:storeId" element={<DashboardShell><RequireMinRole minRole="manager"><StoreDetail /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/products" element={<DashboardShell><RequireMinRole minRole="manager"><Products /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/vendors" element={<DashboardShell><RequireMinRole minRole="manager"><Vendors /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/analytics" element={<DashboardShell><RequireMinRole minRole="manager"><Analytics /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/store-comparison" element={<DashboardShell><RequireMinRole minRole="manager"><StoreComparison /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/waste" element={<DashboardShell><RequireMinRole minRole="manager"><WasteLog /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/transfers" element={<DashboardShell><RequireMinRole minRole="manager"><Transfers /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/recipes" element={<DashboardShell><RequireMinRole minRole="manager"><Recipes /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/settings" element={<DashboardShell><RequireMinRole minRole="manager"><OrgSettings /></RequireMinRole></DashboardShell>} />
              {/* owner only */}
              <Route path="/dashboard/team" element={<DashboardShell><RequireMinRole minRole="owner"><Team /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/subscription" element={<DashboardShell requireSub={false}><RequireMinRole minRole="owner"><Subscription /></RequireMinRole></DashboardShell>} />
              {/* staff+ */}
              <Route path="/dashboard/reorder" element={<DashboardShell><RequireMinRole minRole="staff"><Reorder /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/orders" element={<DashboardShell><RequireMinRole minRole="staff"><Orders /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/deliveries" element={<DashboardShell><RequireMinRole minRole="staff"><Deliveries /></RequireMinRole></DashboardShell>} />
              <Route path="/dashboard/tasks" element={<DashboardShell><RequireMinRole minRole="staff"><Tasks /></RequireMinRole></DashboardShell>} />
              {/* viewer+ (all roles) */}
              <Route path="/dashboard/product-hub" element={<DashboardShell><ProductHub /></DashboardShell>} />
              <Route path="/dashboard/inventory" element={<DashboardShell><Inventory /></DashboardShell>} />
              <Route path="/dashboard/count-history" element={<DashboardShell><CountHistory /></DashboardShell>} />
              <Route path="/dashboard/profile" element={<DashboardShell><Profile /></DashboardShell>} />
              <Route path="/vendor-portal" element={<VendorPortal />} />
              <Route path="/vendor-confirm" element={<VendorConfirm />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ChunkErrorBoundary>
          </AppErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
